import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Users, Mail, Send, RefreshCw, Rocket,
  CheckCircle, XCircle, Clock, AlertTriangle
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import AdminLayout from "@/components/AdminLayout";

const AdminWaitlist = () => {
  const navigate = useNavigate();
  const [waitlistUsers, setWaitlistUsers] = useState([]);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    fetchWaitlistData();
  }, []);

  const fetchWaitlistData = async () => {
    try {
      const [usersRes, countRes] = await Promise.all([
        api.get("/admin/waitlist/users"),
        api.get("/waitlist/count")
      ]);
      setWaitlistUsers(usersRes.data.users || []);
      setWaitlistCount(countRes.data.count || 0);
    } catch (error) {
      toast.error("Failed to load waitlist data");
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    const testEmail = prompt("Enter email address to send test launch email:");
    if (!testEmail) return;

    setTestSending(true);
    try {
      const response = await api.post(`/admin/waitlist/send-launch-emails?test_mode=true&test_email=${encodeURIComponent(testEmail)}`);
      if (response.data.success) {
        toast.success(`Test email sent to ${testEmail}!`);
      } else {
        toast.error(`Failed: ${response.data.error}`);
      }
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setTestSending(false);
    }
  };

  const sendLaunchEmails = async () => {
    setSending(true);
    setShowConfirmModal(false);
    try {
      const response = await api.post("/admin/waitlist/send-launch-emails?test_mode=false");
      setSendResult(response.data);
      toast.success(`Launch emails sent! ${response.data.sent} successful, ${response.data.failed} failed`);
      fetchWaitlistData(); // Refresh to show updated status
    } catch (error) {
      toast.error("Failed to send launch emails");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
      <div className="bg-gray-50 flex items-center justify-center min-h-[calc(100vh-120px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FAB710] border-t-transparent"></div>
      </div>
      </AdminLayout>
    );
  }

  const emailsSent = waitlistUsers.filter(u => u.launch_email_sent).length;
  const emailsPending = waitlistCount - emailsSent;

  return (
    <AdminLayout>
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-[52px] z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1C3A7A]">Waitlist Management</h1>
            <p className="text-sm text-gray-500">Manage pre-launch signups & send launch emails</p>
          </div>
          <button 
            onClick={fetchWaitlistData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="w-10 h-10 bg-[#1C3A7A] rounded-lg flex items-center justify-center mb-3">
              <Users className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-[#1C3A7A]">{waitlistCount}</p>
            <p className="text-sm text-gray-500">Total Signups</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mb-3">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-green-600">{emailsSent}</p>
            <p className="text-sm text-gray-500">Launch Emails Sent</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="w-10 h-10 bg-[#FAB710] rounded-lg flex items-center justify-center mb-3">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-[#FAB710]">{emailsPending}</p>
            <p className="text-sm text-gray-500">Pending Emails</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mb-3">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-purple-600">hello@</p>
            <p className="text-sm text-gray-500">recoverymatch.com</p>
          </div>
        </div>

        {/* Launch Email Actions */}
        <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4a8a] rounded-xl p-6 shadow-lg mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="text-white">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Rocket className="w-6 h-6 text-[#FAB710]" />
                Launch Announcement Emails
              </h2>
              <p className="text-blue-200 mt-1">
                Send the "We're Live!" email to all {waitlistCount} waitlist users
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={sendTestEmail}
                disabled={testSending}
                className="flex items-center justify-center gap-2 px-5 py-3 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors font-semibold disabled:opacity-50"
              >
                {testSending ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Mail className="w-5 h-5" />
                )}
                Send Test Email
              </button>
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={sending || waitlistCount === 0}
                className="flex items-center justify-center gap-2 px-5 py-3 bg-[#FAB710] text-[#1C3A7A] rounded-lg hover:bg-[#f7a000] transition-colors font-bold disabled:opacity-50 shadow-lg"
              >
                {sending ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                Send to All ({waitlistCount})
              </button>
            </div>
          </div>
        </div>

        {/* Send Result */}
        {sendResult && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6 border-l-4 border-green-500">
            <h3 className="font-semibold text-[#1C3A7A] mb-2">Last Send Results</h3>
            <div className="flex gap-6 text-sm">
              <span className="text-green-600">✓ {sendResult.sent} sent</span>
              <span className="text-red-600">✗ {sendResult.failed} failed</span>
              <span className="text-gray-500">Total: {sendResult.total}</span>
            </div>
            {sendResult.errors?.length > 0 && (
              <div className="mt-2 text-xs text-red-500">
                Errors: {sendResult.errors.slice(0, 3).map(e => e.email).join(", ")}
                {sendResult.errors.length > 3 && ` +${sendResult.errors.length - 3} more`}
              </div>
            )}
          </div>
        )}

        {/* Waitlist Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-[#1C3A7A]">Waitlist Users</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">#</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Email</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Joined</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Day One</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Launch Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {waitlistUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-[#FAB710]">#{user.position}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{user.name || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.created_at ? format(new Date(user.created_at), "MMM d, yyyy") : "-"}
                    </td>
                    <td className="px-4 py-3">
                      {user.is_day_one ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#FAB710]/10 text-[#FAB710]">
                          🏆 Day One
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.launch_email_sent ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" /> Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400 text-sm">
                          <Clock className="w-4 h-4" /> Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {waitlistUsers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No waitlist signups yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FAB710]/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-[#FAB710]" />
              </div>
              <div>
                <h3 className="font-bold text-[#1C3A7A]">Confirm Launch Email</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              You are about to send the launch announcement email to <strong>{waitlistCount} users</strong>. 
              Make sure you're ready to go live!
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={sendLaunchEmails}
                className="flex-1 px-4 py-2 bg-[#FAB710] text-[#1C3A7A] rounded-lg hover:bg-[#f7a000] font-bold"
              >
                🚀 Send to All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminWaitlist;
